import React, { useContext, useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Modal, Pressable, StyleSheet, Text, View } from "react-native";
import CampsListView from "./components/views/CampsListView";
import CampsMapView from "./components/views/CampsMapView";
import CampFilterModal from "./components/filters/CampFilterModal";
import SessionFilterModal from "./components/filters/SessionFilterModal";
import ScrollFilter from "./components/filters/ScrollFilter";
import DataOps from "../../data/DataOps";
import LocationOps from "../../utility/LocationOps";
import { GlobalStateContext } from "../../components/general/GlobalStateProvider";
import CampTypes from "../../data/CampTypes";
import LoadingCamps from './components/LoadingCamps';
import SearchOps from '../../data/SearchOps';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildExploreUrl, isExploreLandingPath, parseExploreUrl } from '../../utility/ExploreRouteUtils';

const ExploreScreenMain = ({ isListView: propIsListView, setListView: propSetListView }) => {
    const routeLocation = useLocation();
    const navigate = useNavigate();
    const isLocalTestingEnv = typeof window !== 'undefined' && (
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1'
    );
    const [campEnvironmentView, setCampEnvironmentView] = useState<'test' | 'prod'>('prod');
    const [isSessionView, setIsSessionView] = useState(() => {
        try {
            const saved = typeof localStorage !== 'undefined' ? localStorage.getItem('isSessionView') : null;
            return saved ? JSON.parse(saved) : false;
        } catch {
            return false;
        }
    });
    const [isCampFilterModalVisible, setCampFilterModalVisible] = useState(false);
    const [isSessionFilterModalVisible, setSessionFilterModalVisible] = useState(false);

    const modalRef = useRef(null);


    const [location, setLocation] = useState(null);
    const [camps, setCamps] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [isFetching, setFetching] = useState(false);
    const [isFetchingSessions, setFetchingSessions] = useState(false);
    const [selectedCamp, setSelectedCamp] = useState(null);
    const [cachedCamps, setCachedCamps] = useState([]);
    const [cachedSessions, setCachedSessions] = useState([])
    // Camps filter modal states.
    const [selectedCategories, setSelectedCategories] = useState(() => {
        try { const v = typeof localStorage !== 'undefined' ? localStorage.getItem('explore_selectedCategories') : null; return v ? JSON.parse(v) : []; } catch { return []; }
    });
    const [price, setPrice] = useState(1000);
    const [distance, setDistance] = useState(30);
    const [ratingRange, setRatingRange] = useState(() => {
        try { const v = typeof localStorage !== 'undefined' ? localStorage.getItem('explore_ratingRange') : null; return v ? JSON.parse(v) : [0, 5]; } catch { return [0, 5]; }
    });
    const [distanceRange, setDistanceRange] = useState(() => {
        try { const v = typeof localStorage !== 'undefined' ? localStorage.getItem('explore_distanceRange') : null; return v ? JSON.parse(v) : [0, 300]; } catch { return [0, 300]; }
    });
    const [showActiveSessions, setShowActiveSessions] = useState(() => {
        try { const v = typeof localStorage !== 'undefined' ? localStorage.getItem('explore_showActiveSessions') : null; return v ? JSON.parse(v) : false; } catch { return false; }
    });
    // Sessions filter modal states.
    const [selectedSessionTypes, setSelectedSessionTypes] = useState(() => {
        try { const v = typeof localStorage !== 'undefined' ? localStorage.getItem('explore_selectedSessionTypes') : null; return v ? JSON.parse(v) : []; } catch { return []; }
    });
    const [showAvailableOnly, setShowAvailableOnly] = useState(() => {
        try { const v = typeof localStorage !== 'undefined' ? localStorage.getItem('explore_showAvailableOnly') : null; return v ? JSON.parse(v) : false; } catch { return false; }
    });
    const [classSizeValues, setClassSizeValues] = useState(() => {
        try { const v = typeof localStorage !== 'undefined' ? localStorage.getItem('explore_classSizeValues') : null; return v ? JSON.parse(v) : [1, 100]; } catch { return [1, 100]; }
    });
    const [sessionPriceValues, setSessionPriceValues] = useState(() => {
        try { const v = typeof localStorage !== 'undefined' ? localStorage.getItem('explore_sessionPriceValues') : null; return v ? JSON.parse(v) : [0, 2000]; } catch { return [0, 2000]; }
    });
    const [sessionAgeValues, setSessionAgeValues] = useState(() => {
        try { const v = typeof localStorage !== 'undefined' ? localStorage.getItem('explore_sessionAgeValues') : null; return v ? JSON.parse(v) : [0, 30]; } catch { return [0, 30]; }
    })

    const {
        user: user,
        setLikedCamps: setLikedCamps,
        globalCamps: globalCamps,
        globalSessions: globalSessions,
        setGlobalCamps: setGlobalCamps,
        setGlobalSessions: setGlobalSessions,
        globalLocation: globalLocation,
        setGlobalLocation: setGlobalLocation,
        globalMapCenter: globalMapCenter,
        setGlobalMapCenter: setGlobalMapCenter,
        globalSearch: globalSearch,
        setGlobalSearch: setGlobalSearch,
        isMapView: globalIsMapView,
        setIsMapView: setGlobalIsMapView,
    } = useContext(GlobalStateContext);

    const isListView = !globalIsMapView;
    const setListView = (isListViewValue: boolean) => {
        setGlobalIsMapView(!isListViewValue);
        if (propSetListView) {
            propSetListView(isListViewValue);
        }
    };

    const parsedRoute = useMemo(
        () => parseExploreUrl(routeLocation.pathname, routeLocation.search),
        [routeLocation.pathname, routeLocation.search]
    );
    const routeKey = `${routeLocation.pathname}${routeLocation.search}`;
    const shouldBootstrapFromUrl = parsedRoute.hasShareableState || isExploreLandingPath(routeLocation.pathname);
    const isApplyingRouteRef = useRef(false);
    const lastAppliedRouteKeyRef = useRef<string | null>(null);

    const arraysAreEqual = (arr1, arr2) => {
        return JSON.stringify(arr1) === JSON.stringify(arr2);
    };

    const getFilterCount = () => {
        let filterCount = 0;
        if (selectedCategories.length > 0) {
            filterCount += 1
        }

        if (selectedSessionTypes.length > 0) {
            filterCount += 1
        }
        if (showAvailableOnly) {
            filterCount += 1
        }
        if (!arraysAreEqual(classSizeValues, [1, 100])) {
            filterCount += 1
        }
        if (!arraysAreEqual(sessionPriceValues, [0, 2000])) {
            filterCount += 1
        }
        if (!arraysAreEqual(sessionAgeValues, [0, 30])) {
            filterCount += 1
        }

        // Camp filter counts
        if (!arraysAreEqual(ratingRange, [0, 5])) {
            filterCount += 1
        }
        if (!arraysAreEqual(distanceRange, [0, 300])) {
            filterCount += 1
        }
        if (showActiveSessions) {
            filterCount += 1
        }
        if (price !== 1000) {
            filterCount += 1
        }
        if (distance !== 30) {
            filterCount += 1
        }
        return filterCount;
    }

    const hasActiveFilters = () => {
        return getFilterCount() > 0;
    }

    const clearAllFilters = () => {
        // Reset camp filters
        setSelectedCategories([]);
        setPrice(1000);
        setDistance(30);
        setRatingRange([0, 5]);
        setDistanceRange([0, 300]);
        setShowActiveSessions(false);

        // Reset session filters
        setSelectedSessionTypes([]);
        setShowAvailableOnly(false);
        setClassSizeValues([1, 100]);
        setSessionPriceValues([0, 2000]);
        setSessionAgeValues([0, 30]);

        // Restore cached data, but always respect environment visibility rules.
        const envFilteredCamps = (cachedCamps || []).filter(matchesCampEnvironment);
        const envCampIds = new Set(envFilteredCamps.map((camp: any) => camp.campId));
        const envFilteredSessions = (cachedSessions || []).filter((session: any) => envCampIds.has(session.campId));

        setCamps(envFilteredCamps);
        setSessions(envFilteredSessions);
    };

    const matchesCampEnvironment = (camp: any) => {
        const environment = String(camp?.environment || '').toLowerCase();
        const isTestFlag = camp?.isTestCamp === true;
        const isProdFlag = camp?.isProdCamp === true;
        const isExplicitTest = environment === 'test' || isTestFlag;

        // On production/staging websites, never show test camps.
        if (!isLocalTestingEnv) {
            return !isExplicitTest;
        }

        if (campEnvironmentView === 'prod') {
            return environment === 'prod' || isProdFlag;
        }

        // Local test view: include explicit test and legacy unset records.
        return isExplicitTest || (!environment && !isProdFlag);
    };

    const DEFAULT_RADIUS_MILES = 20;

    const getLocationWithTimeout = async (ms: number) => {
        try {
            return await Promise.race([
                LocationOps.getCurrentLocation(),
                new Promise((resolve) => setTimeout(() => resolve(null), ms))
            ]);
        } catch {
            return null;
        }
    };

    const runShareableRouteSearch = useCallback(async () => {
        const routeAddress = parsedRoute.address;
        const hasLocation = !!routeAddress;
        const nextSearchSummary = {
            address: hasLocation ? routeAddress : 'Nearby',
            startDate: parsedRoute.startDate || null,
            endDate: parsedRoute.endDate || null,
            age: parsedRoute.age ?? null,
            gender: parsedRoute.gender ?? null,
        };

        setGlobalSearch(nextSearchSummary);

        const searchCriteria: any = {};
        if (hasLocation) {
            searchCriteria.location = routeAddress;
            searchCriteria.maxDistance = DEFAULT_RADIUS_MILES;
            try {
                const geocoded = await LocationOps.getLocationFromAddress(routeAddress);
                if (geocoded?.lat && geocoded?.lng) {
                    setGlobalMapCenter({ lat: geocoded.lat, lng: geocoded.lng });
                }
            } catch { }
        } else {
            let currentLocation = globalLocation;

            if (!currentLocation?.coords) {
                currentLocation = await getLocationWithTimeout(5 * 1000);
            }

            if (currentLocation?.coords) {
                const { latitude, longitude } = currentLocation.coords;
                searchCriteria.location = `${latitude},${longitude}`;
                searchCriteria.maxDistance = DEFAULT_RADIUS_MILES;
                setGlobalLocation({
                    coords: { latitude, longitude },
                    timestamp: currentLocation.timestamp || Date.now(),
                }, true);
            }
        }

        if (parsedRoute.startDate && parsedRoute.endDate) {
            searchCriteria.startDate = parsedRoute.startDate;
            searchCriteria.endDate = parsedRoute.endDate;
        }

        if (typeof parsedRoute.age === 'number') {
            searchCriteria.age = parsedRoute.age;
        }

        if (parsedRoute.gender) {
            searchCriteria.gender = parsedRoute.gender;
        }

        if (!searchCriteria.location && !searchCriteria.startDate && typeof searchCriteria.age !== 'number') {
            return;
        }

        setFetching(true);
        try {
            const searchResults = await SearchOps.searchCamps(searchCriteria);
            setGlobalCamps(searchResults);
            setCachedCamps(searchResults);
            setCamps(searchResults);
        } finally {
            setFetching(false);
        }

        if (parsedRoute.view === 'sessions' || parsedRoute.startDate || typeof parsedRoute.age === 'number') {
            setFetchingSessions(true);
            try {
                const sessionResults = await SearchOps.searchSessions(searchCriteria);
                setGlobalSessions(sessionResults);
                setCachedSessions(sessionResults);
                setSessions(sessionResults);
            } finally {
                setFetchingSessions(false);
            }
        }
    }, [
        DEFAULT_RADIUS_MILES,
        globalLocation,
        parsedRoute.address,
        parsedRoute.age,
        parsedRoute.endDate,
        parsedRoute.gender,
        parsedRoute.startDate,
        parsedRoute.view,
        setGlobalCamps,
        setGlobalLocation,
        setGlobalMapCenter,
        setGlobalSearch,
        setGlobalSessions,
    ]);

    const fetchCampsNearby = async (forceRefetch = false) => {
        setFetching(true);
        try {
            let center = null as any;
            let currentLocation = null as any;

            // Prioritize cached map center (where user was viewing)
            if (globalMapCenter && !forceRefetch) {
                center = globalMapCenter;
                if (globalLocation) {
                    setLocation(globalLocation);
                }
            } else if (globalLocation && !forceRefetch) {
                // Use cached physical location
                center = {
                    lat: globalLocation.coords.latitude,
                    lng: globalLocation.coords.longitude
                };
                currentLocation = globalLocation;
                setLocation(globalLocation);
            } else {
                // Fetch fresh physical location (only on cold start or forced refresh)
                currentLocation = await getLocationWithTimeout(5 * 1000);
                if (currentLocation?.coords) {
                    center = {
                        lat: currentLocation.coords.latitude,
                        lng: currentLocation.coords.longitude
                    };
                    // Save physical location with timestamp and update map center
                    const locationWithTimestamp = {
                        ...currentLocation,
                        timestamp: Date.now()
                    };
                    setLocation(locationWithTimestamp);
                    setGlobalLocation(locationWithTimestamp, true);
                } else {
                    // Fallback to Times Square if geolocation fails or times out
                    center = { lat: 40.758, lng: -73.9855 };
                }
            }

            const rawCamps = await DataOps.getCampsWithinRadius(center, DEFAULT_RADIUS_MILES);

            const filtered = await Promise.all(
                rawCamps.map(async (camp) => {
                    const activeCount = camp.activeSessionCount != null
                        ? camp.activeSessionCount
                        : await DataOps.getUpcomingSessionCount(camp.campId);
                    return { ...camp, activeSessionCount: activeCount };
                })
            );
            filtered.sort((a, b) => {
                const diff = (b.activeSessionCount || 0) - (a.activeSessionCount || 0);
                if (diff !== 0) return diff;
                if (a.distance == null && b.distance == null) return 0;
                if (a.distance == null) return 1;
                if (b.distance == null) return -1;
                return a.distance - b.distance;
            });

            setCamps(filtered);
            setCachedCamps(filtered);
            setGlobalCamps(filtered);
        } finally {
            setFetching(false);
        }
    };

    const fetchSessions = async () => {
        setFetchingSessions(true);
        try {
            const sessionsData = await DataOps.fetchUpcomingSessionsFromFirebase();
            setSessions(sessionsData);
            setCachedSessions(sessionsData);
            setGlobalSessions(sessionsData);
        } finally {
            setFetchingSessions(false);
        }
    };

    const fetchLikedCamps = async () => {
        try {
            if (!user?.userId) return;
            const userData = await DataOps.fetchDataFromFirebase('user', user.userId);
            setLikedCamps(new Set((userData && userData.likedCamps) ? userData.likedCamps : []));
        } catch { }
    }

    useEffect(() => {
        setCamps(globalCamps);
        setCachedCamps(globalCamps);
    }, [globalCamps]);

    useEffect(() => {
        setSessions(globalSessions);
        setCachedSessions(globalSessions);
    }, [globalSessions]);

    useEffect(() => {
        if (shouldBootstrapFromUrl) {
            return;
        }

        // Only fetch camps if we don't have cached camps and location/map center
        // If we have cached data, the map will handle the search
        const shouldFetchCamps = globalCamps.length === 0 || (!globalLocation && !globalMapCenter);

        if (shouldFetchCamps) {
            (async () => {
                const unused_camps_response = await fetchCampsNearby();
            })();
        } else {
            // We have cached camps and location, just set the location state
            if (globalLocation) {
                setLocation(globalLocation);
            }
        }
    }, [globalCamps.length, globalLocation, globalMapCenter, shouldBootstrapFromUrl]); // Route-based searches skip this bootstrap

    useEffect(() => {
        if (!shouldBootstrapFromUrl) {
            return;
        }

        if (lastAppliedRouteKeyRef.current === routeKey) {
            return;
        }

        let isActive = true;
        lastAppliedRouteKeyRef.current = routeKey;
        isApplyingRouteRef.current = true;

        const applyRouteState = async () => {
            try {
                const currentSummaryAddress = globalSearch?.address || 'Nearby';
                const routeSummaryAddress = parsedRoute.address || 'Nearby';
                const routeMatchesCurrentSearch =
                    currentSummaryAddress === routeSummaryAddress
                    && (globalSearch?.age ?? null) === (parsedRoute.age ?? null)
                    && (globalSearch?.gender ?? null) === (parsedRoute.gender ?? null)
                    && (globalSearch?.startDate ? new Date(globalSearch.startDate).toISOString().slice(0, 10) : null)
                        === (parsedRoute.startDate ? parsedRoute.startDate.toISOString().slice(0, 10) : null)
                    && (globalSearch?.endDate ? new Date(globalSearch.endDate).toISOString().slice(0, 10) : null)
                        === (parsedRoute.endDate ? parsedRoute.endDate.toISOString().slice(0, 10) : null)
                    && arraysAreEqual(selectedCategories, parsedRoute.categories || []);

                if (parsedRoute.view === 'sessions' && !isSessionView) {
                    setIsSessionView(true);
                } else if (parsedRoute.view !== 'sessions' && isSessionView) {
                    setIsSessionView(false);
                }

                if (!arraysAreEqual(selectedCategories, parsedRoute.categories || [])) {
                    setSelectedCategories(parsedRoute.categories || []);
                }

                if (routeMatchesCurrentSearch && globalCamps.length > 0) {
                    return;
                }

                await runShareableRouteSearch();
            } finally {
                if (isActive) {
                    isApplyingRouteRef.current = false;
                }
            }
        };

        const promise = applyRouteState();

        return () => {
            isActive = false;
            void promise;
        };
    }, [
        globalCamps.length,
        globalSearch?.address,
        globalSearch?.age,
        globalSearch?.endDate,
        globalSearch?.gender,
        globalSearch?.startDate,
        isSessionView,
        parsedRoute.categories,
        parsedRoute.view,
        routeKey,
        runShareableRouteSearch,
        selectedCategories,
        shouldBootstrapFromUrl,
    ]);

    useEffect(() => {
        try {
            const ev = new CustomEvent('exploreSessionViewChanged', { detail: { isSessionView } });
            window.dispatchEvent(ev);
        } catch { }

        // Always refresh when entering Session view so recently created sessions appear.
        if (isSessionView && !isFetchingSessions) {
            const unused_sessions_response = fetchSessions();
        }
    }, [isSessionView]);

    // Persist session/camp view preference across refreshes
    useEffect(() => {
        try {
            if (typeof localStorage !== 'undefined') {
                localStorage.setItem('isSessionView', JSON.stringify(isSessionView));
            }
        } catch {}
    }, [isSessionView]);

    // Persist filter states across refreshes
    useEffect(() => { try { localStorage.setItem('explore_selectedCategories', JSON.stringify(selectedCategories)); } catch {} }, [selectedCategories]);
    useEffect(() => { try { localStorage.setItem('explore_ratingRange', JSON.stringify(ratingRange)); } catch {} }, [ratingRange]);
    useEffect(() => { try { localStorage.setItem('explore_distanceRange', JSON.stringify(distanceRange)); } catch {} }, [distanceRange]);
    useEffect(() => { try { localStorage.setItem('explore_showActiveSessions', JSON.stringify(showActiveSessions)); } catch {} }, [showActiveSessions]);
    useEffect(() => { try { localStorage.setItem('explore_selectedSessionTypes', JSON.stringify(selectedSessionTypes)); } catch {} }, [selectedSessionTypes]);
    useEffect(() => { try { localStorage.setItem('explore_showAvailableOnly', JSON.stringify(showAvailableOnly)); } catch {} }, [showAvailableOnly]);
    useEffect(() => { try { localStorage.setItem('explore_classSizeValues', JSON.stringify(classSizeValues)); } catch {} }, [classSizeValues]);
    useEffect(() => { try { localStorage.setItem('explore_sessionPriceValues', JSON.stringify(sessionPriceValues)); } catch {} }, [sessionPriceValues]);
    useEffect(() => { try { localStorage.setItem('explore_sessionAgeValues', JSON.stringify(sessionAgeValues)); } catch {} }, [sessionAgeValues]);

    useEffect(() => {
        if (routeLocation.pathname === '/' || isApplyingRouteRef.current) {
            return;
        }

        const nextUrl = buildExploreUrl({
            address: globalSearch?.address,
            startDate: globalSearch?.startDate ? new Date(globalSearch.startDate) : null,
            endDate: globalSearch?.endDate ? new Date(globalSearch.endDate) : null,
            age: globalSearch?.age ?? null,
            gender: globalSearch?.gender ?? null,
            view: isSessionView ? 'sessions' : 'camps',
            categories: selectedCategories,
        });

        if (nextUrl !== routeKey) {
            navigate(nextUrl, { replace: true });
        }
    }, [
        globalSearch?.address,
        globalSearch?.age,
        globalSearch?.endDate,
        globalSearch?.gender,
        globalSearch?.startDate,
        isSessionView,
        navigate,
        routeKey,
        routeLocation.pathname,
        selectedCategories,
    ]);

    useEffect(() => {
        (async () => {
            const unused_response = await fetchLikedCamps();
        })();
    }, [user]);

    useEffect(() => {
        if (!isSessionView) {
            if (!cachedCamps) return;

            let filtered = [...cachedCamps];
            filtered = filtered.filter(matchesCampEnvironment);

            if (selectedCategories && selectedCategories.length > 0) {
                filtered = filtered.filter(camp => selectedCategories.map(text => CampTypes.find(type => type.text === text)?.iconName).filter(Boolean).includes(camp.campCategory));
            }

            if (distanceRange && (distanceRange[0] > 0 || distanceRange[1] < 300)) {
                filtered = filtered.filter(camp => {
                    if (camp.distance === undefined || camp.distance === null) return true;
                    return camp.distance >= distanceRange[0] && camp.distance <= distanceRange[1];
                });
            }

            if (ratingRange && (ratingRange[0] > 0 || ratingRange[1] < 5)) {
                filtered = filtered.filter(camp => {
                    const rating = camp.rating || 5.0;
                    return rating >= ratingRange[0] && rating <= ratingRange[1];
                });
            }

            // Upcoming sessions only
            if (showActiveSessions) {
                filtered = filtered.filter(camp => camp.activeSessionCount > 0);
            }

            setCamps(filtered);
        } else {
            if (!cachedSessions) return;

            const envCampIds = new Set(
                (cachedCamps || []).filter(matchesCampEnvironment).map((camp: any) => camp.campId)
            );

            if (!selectedCategories || selectedCategories.length === 0) {
                setSessions((cachedSessions || []).filter((session: any) => envCampIds.has(session.campId)));
            } else {
                const filteredSessions = cachedSessions.filter(session => {
                    if (!envCampIds.has(session.campId)) return false;
                    const associatedCamp = cachedCamps.find(camp => camp.campId === session.campId);
                    return associatedCamp && selectedCategories.map(text => CampTypes.find(type => type.text === text)?.iconName).filter(Boolean).includes(associatedCamp.campCategory);
                });
                setSessions(filteredSessions);
            }
        }
    }, [
        isSessionView,
        cachedCamps,
        cachedSessions,
        selectedCategories,
        distanceRange,
        ratingRange,
        showActiveSessions,
        campEnvironmentView,
    ]);

    const envFilteredCampsForSessionFilters = useMemo(
        () => (cachedCamps || []).filter(matchesCampEnvironment),
        [cachedCamps, campEnvironmentView]
    );

    const envFilteredCachedSessionsForSessionFilters = useMemo(() => {
        const envCampIds = new Set(
            envFilteredCampsForSessionFilters.map((camp: any) => camp.campId)
        );
        return (cachedSessions || []).filter((session: any) => envCampIds.has(session.campId));
    }, [cachedSessions, envFilteredCampsForSessionFilters]);

    // Handle clicking outside modal to close it
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                if (isCampFilterModalVisible) {
                    setCampFilterModalVisible(false);
                }
                if (isSessionFilterModalVisible) {
                    setSessionFilterModalVisible(false);
                }
            }
        };

        if (isCampFilterModalVisible || isSessionFilterModalVisible) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isCampFilterModalVisible, isSessionFilterModalVisible]);

    return (
        <View style={styles.container}>
            <View style={styles.stickyFilter}>
                <ScrollFilter
                    isSessionView={isSessionView}
                    setIsSessionView={setIsSessionView}
                    setFilterModalVisible={isSessionView ? setSessionFilterModalVisible : setCampFilterModalVisible}
                    filterCount={getFilterCount()}
                    selectedCategories={selectedCategories}
                    setSelectedCategories={setSelectedCategories}
                />
                {isLocalTestingEnv && (
                    <View style={styles.localEnvToggleRow}>
                        <Text style={styles.localEnvLabel}>Camp Environment</Text>
                        <View style={styles.localEnvToggle}>
                            <Pressable
                                onPress={() => setCampEnvironmentView('test')}
                                style={[
                                    styles.localEnvOption,
                                    campEnvironmentView === 'test' && styles.localEnvOptionActive
                                ]}
                            >
                                <Text style={[
                                    styles.localEnvOptionText,
                                    campEnvironmentView === 'test' && styles.localEnvOptionTextActive
                                ]}>
                                    Test
                                </Text>
                            </Pressable>
                            <Pressable
                                onPress={() => setCampEnvironmentView('prod')}
                                style={[
                                    styles.localEnvOption,
                                    campEnvironmentView === 'prod' && styles.localEnvOptionActive
                                ]}
                            >
                                <Text style={[
                                    styles.localEnvOptionText,
                                    campEnvironmentView === 'prod' && styles.localEnvOptionTextActive
                                ]}>
                                    Prod
                                </Text>
                            </Pressable>
                        </View>
                    </View>
                )}
            </View>
            {!isSessionView && isFetching && camps.length === 0 && (
                <LoadingCamps isLocating={!location} radius={DEFAULT_RADIUS_MILES} mode="camps" />
            )}
            {isSessionView && isFetchingSessions && (
                <LoadingCamps mode="sessions" />
            )}
            {
                isListView
                    ? (
                        <CampsListView
                            sessions={sessions}
                            isSessionView={isSessionView}
                            camps={camps}
                            hasSearchCriteria={hasActiveFilters()}
                            onClearSearch={clearAllFilters}
                            showEmptyState={isListView}
                        />
                    )
                    : <CampsMapViewWrapper
                        isSessionView={isSessionView}
                        sessions={sessions}
                        cachedCamps={cachedCamps}
                        camps={camps}
                        setSelectedCamp={setSelectedCamp}
                        setCamps={setCamps}
                        setGlobalCamps={setGlobalCamps}
                    />
            }
            <Modal
                visible={isSessionView ? isSessionFilterModalVisible : isCampFilterModalVisible}
                animationType="slide"
                onRequestClose={() => {
                    isSessionView ? setSessionFilterModalVisible(false) : setCampFilterModalVisible(false)
                }}
                transparent={true}
            >
                <View style={styles.modalOverlay}>
                    <View
                        ref={modalRef}
                        style={styles.modalContent}
                    >
                        {
                            !isSessionView
                                ? <CampFilterModal
                                    cachedCamps={envFilteredCampsForSessionFilters}
                                    setCamps={setCamps}
                                    handleModalClose={() => {
                                        setCampFilterModalVisible(false);
                                    }}
                                    selectedCategories={selectedCategories}
                                    setSelectedCategories={setSelectedCategories}
                                    ratingRange={ratingRange}
                                    setRatingRange={setRatingRange}
                                    distanceRange={distanceRange}
                                    setDistanceRange={setDistanceRange}
                                    showActiveSessions={showActiveSessions}
                                    setShowActiveSessions={setShowActiveSessions}
                                />
                                : <SessionFilterModal
                                    sessions={sessions}
                                    cachedSessions={envFilteredCachedSessionsForSessionFilters}
                                    setSessions={setSessions}
                                    handleModalClose={() => {
                                        setSessionFilterModalVisible(false);
                                    }}
                                    selectedSessionTypes={selectedSessionTypes}
                                    setSelectedSessionTypes={setSelectedSessionTypes}
                                    selectedCategories={selectedCategories}
                                    setSelectedCategories={setSelectedCategories}
                                    cachedCamps={envFilteredCampsForSessionFilters}
                                    showAvailableOnly={showAvailableOnly}
                                    setShowAvailableOnly={setShowAvailableOnly}
                                    classSizeValues={classSizeValues}
                                    setClassSizeValues={setClassSizeValues}
                                    sessionPriceValues={sessionPriceValues}
                                    setSessionPriceValues={setSessionPriceValues}
                                    sessionAgeValues={sessionAgeValues}
                                    setSessionAgeValues={setSessionAgeValues}
                                />
                        }
                    </View>
                </View>
            </Modal>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        paddingHorizontal: 50,
        minHeight: 700,
    },
    stickyFilter: {
        position: 'sticky' as any,
        top: 0,
        zIndex: 15,
        backgroundColor: 'white',
        width: '100%',
    },
    localEnvToggleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 8,
        paddingTop: 4,
        paddingBottom: 10,
    },
    localEnvLabel: {
        fontSize: 12,
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        color: '#6B7280',
    },
    localEnvToggle: {
        flexDirection: 'row',
        backgroundColor: '#F3F4F6',
        borderRadius: 999,
        padding: 3,
    },
    localEnvOption: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 999,
    },
    localEnvOptionActive: {
        backgroundColor: '#111827',
    },
    localEnvOptionText: {
        fontSize: 12,
        fontWeight: '700',
        color: '#4B5563',
    },
    localEnvOptionTextActive: {
        color: '#FFFFFF',
    },
    loadingState: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 60,
    },
    loadingTextWrap: {
        alignItems: 'center',
        justifyContent: 'center',
    },
    modalOverlay: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
    modalContent: {
        width: 800,
        height: 750, // Much taller to accommodate all filters
        maxHeight: '95%', // Allow almost full screen height if needed
        backgroundColor: '#fff',
        borderRadius: 20,
        padding: 20,
        elevation: 5,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.3,
        shadowRadius: 5,
    },
});

// Wrapper to prevent re-creating callback on every render
function CampsMapViewWrapper({ isSessionView, sessions, cachedCamps, camps, setSelectedCamp, setCamps, setGlobalCamps }: {
    isSessionView: boolean;
    sessions: any[];
    cachedCamps: any[];
    camps: any[];
    setSelectedCamp: any;
    setCamps: any;
    setGlobalCamps: any;
}) {
    const campsForMap = isSessionView
        ? (() => {
            const ids = new Set((sessions || []).map(s => s.campId));
            return (cachedCamps || []).filter(c => ids.has(c.campId));
        })()
        : camps;

    // Handler for when map search updates camps (memoized)
    const handleMapCampsUpdate = useCallback((newCamps) => {
        if (!isSessionView) {
            setCamps(newCamps);
            setGlobalCamps(newCamps);
        }
    }, [isSessionView, setCamps, setGlobalCamps]);

    return (
        <CampsMapView
            camps={campsForMap}
            setSelectedCamp={setSelectedCamp}
            onMapCampsUpdate={handleMapCampsUpdate}
        />
    );
}

export default ExploreScreenMain;